<script lang="ts">
  import Layout from "$app/Layouts/Plan.svelte"
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { xhr, toast } from "$app/utils"
  import Members from "./Members.svelte"
  import Billing from "./Billing.svelte"
  import Subscription from "./Subscription.svelte"
  import { signupStore } from "$app/signupstore.svelte"
  import { appStore } from "$app/store.svelte"
  import { Box, CreditCard, Users } from "lucide-svelte"

  type Props = {
    plan: PlanType
    current_user: UserType
    subscription: SubscriptionType
    users: UserType[]
    stripe_publish_key: string
    extra: any
  }

  let {
    plan,
    current_user,
    subscription,
    users,
    stripe_publish_key,
    ...extra
  }: Props = $props()

  appStore.plan = plan
  appStore.current_user = current_user
  signupStore.subscription = subscription
  signupStore.stripe_publish_key = stripe_publish_key

  signupStore.billingInterval = subscription.billing_interval
  if (subscription.subscription_type === "free") {
    signupStore.billingInterval = "year"
  }

  appStore.users = Object.values(users)

  type tabType = {
    title: string
    active: boolean
    slug: string
  }
  let tabs = $state([
    {
      title: "Subscription",
      active: true,
      slug: "subscription",
      icon: Box
    },
    {
      title: "Billing",
      active: false,
      slug: "billing",
      icon: CreditCard
    },
    {
      title: "Members",
      active: false,
      slug: "members",
      icon: Users
    }
  ])

  //let activeTabSlug = tabs.find((v) => v.active)?.slug

  function updateTab(tab: tabType): void {
    const activeTab = tabs.find((t) => t.active === true)
    activeTab.active = false
    tab.active = true
    tabs = tabs
  }

  let isLoading = $state(true)
  async function initialize(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get<{
        users: Record<number, UserType>
        stripe_products: StripeProductType[]
        stripe_data: StripeDataType
      }>(`/plans/${appStore.plan.id}/admin/billing`)

      signupStore.products = resp.data.stripe_products
      signupStore.stripe_publish_key = resp.data.stripe_data.publish_key
      signupStore.stripe_data = resp.data.stripe_data
    } catch (e) {
      console.log(e)
      toast("There was an error loading the billing data", "error")
    } finally {
      isLoading = false
    }
  }

  onMount(() => {
    initialize()
    var hash = window.location.hash.substr(1)
    if (hash) {
      const tab = tabs.find((t) => t.slug.replace(/\s+/g, "") === hash)
      if (tab) {
        updateTab(tab)
      }
    }
  })
</script>

<Layout {current_user} {plan} {users} {subscription} {...extra}>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- We've used 3xl here, but feel free to try other max-widths based on your needs -->
    <div class="max-w-5xl mx-auto">
      <div class="w-full py-6 flex flex-col justify-center">
        <h1 class="text-3xl text-center text-bold w-full text-secondary">
          Billing and Subscription
        </h1>
      </div>
    </div>

    <div class="mx-auto max-w-7xl pb-4">
      <div class="bg-white rounded shadow lg:flex lg:gap-x-8 lg:px-4">
        <aside
          class="flex overflow-x-auto border-b border-gray-900/5 py-4 lg:block lg:w-64 lg:flex-none lg:py-12 border-r pr-4"
        >
          <nav class="flex-none px-4 sm:px-6 lg:px-0">
            <ul
              role="list"
              class="flex gap-x-3 gap-y-1 whitespace-nowrap lg:flex-col"
            >
              {#each tabs as tab (tab.slug)}
                {@const SvelteComponent = tab.icon}

                <li>
                  <!-- Current: "bg-gray-50 text-indigo-600", Default: "text-gray-700 hover:text-indigo-600 hover:bg-gray-50" -->
                  <a
                    href="#{tab.slug}"
                    onclick={() => updateTab(tab)}
                    class="w-full group flex gap-x-3 rounded-md py-2 pl-2 pr-3 text-base font-semibold {tab.active
                      ? 'text-indigo-600 bg-gray-50'
                      : ' text-gray-700 hover:bg-gray-50 hover:text-indigo-500'}"
                  >
                    <SvelteComponent
                      class="size-6 shrink-0 {tab.active
                        ? 'text-indigo-600'
                        : 'text-gray-400 group-hover:text-indigo-600'}"
                    />

                    {tab.title}
                  </a>
                </li>
              {/each}
            </ul>
          </nav>
        </aside>

        <main class="px-4 py-8 sm:px-6 lg:flex-auto lg:px-0 lg:py-12">
          <div
            class="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none"
          >
            {#if isLoading}
              <div class="py-12 flex justify-center w-full">
                <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
              </div>
            {:else}
              <div>
                {#if tabs[0].active}
                  <Subscription />
                {:else if tabs[1].active}
                  <Billing />
                {:else if tabs[2].active}
                  <Members />
                {/if}
              </div>
            {/if}
          </div>
        </main>
      </div>
    </div>
  </div>
</Layout>
